// internal-deps
import { CheckoutRoute, SwgyHeaderKeysList } from "@swiggy-private/checkout-types";

// utils
import { GlobalHeaderState } from "@checkout-core/utils";

// constants
import { SOURCE_CHANNEL } from "@checkout-core/constants/Channel";
import {
    IXIGO_PACKAGE_TO_UPI_SCHEME_MAP,
    IXIGO_UPI_SCHEME_TO_PACKAGE_MAP,
} from "@checkout-core/constants/MwebUpiAppWhitelist";
import { RouteContextValue, isAppPlatform } from "@swiggy-private/core-ui";
import { ADDITIONAL_API_HEADERS_FOR_PL_APIS } from "../constants";
import { isWebPlatform } from "./getSwiggyHeaders";

export const isPopFlow = (): boolean => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) === SOURCE_CHANNEL.POP_SHELL_APP;
};

export const isToingFlow = (): boolean => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) === SOURCE_CHANNEL.TOING_APP;
};

export const isJioHotstarFlow = (): boolean => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) === SOURCE_CHANNEL.JIO_HOTSTAR;
};

export const isIxigoFlow = (): boolean => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) === SOURCE_CHANNEL.IXIGO;
};

/**
 * mweb/dweb checkout: UPI intent availability from GPO (BE), not device NB or MwebUpiAppWhitelist.
 * Ixigo with partner bridge keeps device-based availability via WebBridge.
 */
export const shouldUseGpoUpiIntentAppsOnWebCheckout = (): boolean => {
    if (isIxigoFlow() && isIxigoUpiBridgeAvailable()) {
        return false;
    }
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    return isWebPlatform(platform);
};

/**
 * Check if Ixigo UPI bridge methods are available
 * Used to determine if we should use Ixigo's native UPI handlers
 */
export const isIxigoUpiBridgeAvailable = (): boolean => {
    const ixigoBridge = typeof window !== "undefined" ? window.ixigoBridge : undefined;
    return typeof ixigoBridge?.getUPIAppsList === "function";
};

export const ixigoExpandToAllFormats = (identifier: string): string[] => {
    const scheme = IXIGO_PACKAGE_TO_UPI_SCHEME_MAP[identifier];
    if (scheme) {
        return [identifier, scheme];
    }

    const pkg = IXIGO_UPI_SCHEME_TO_PACKAGE_MAP[identifier];
    if (pkg) {
        return [identifier, pkg];
    }

    return [identifier];
};

/**
 * Resolve a method identifier to an Android package name for `openUPIApp`.
 * Handles both scheme URLs and package names as input.
 */
export const ixigoResolveToPackageName = (methodName: string): string => {
    if (methodName in IXIGO_PACKAGE_TO_UPI_SCHEME_MAP) {
        return methodName;
    }

    return IXIGO_UPI_SCHEME_TO_PACKAGE_MAP[methodName] || methodName;
};

export const isNotCartInitialRoute = (route: RouteContextValue<string>["route"]): boolean => {
    return route?.name !== CheckoutRoute.CART && route?.name !== CheckoutRoute.TOING_CART;
};

export const navigateToCart = (
    navigateTo: RouteContextValue<CheckoutRoute>["navigateTo"],
    data?: {},
    replace?: boolean,
): void => {
    const xChannel = GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel);

    if (xChannel === SOURCE_CHANNEL.TOING_APP) {
        navigateTo(CheckoutRoute.TOING_CART, data, replace);
    } else {
        navigateTo(CheckoutRoute.CART, data, replace);
    }
};

export const addAdditionalHeadersToPLAPI = () => {
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    const isAppFlow = isAppPlatform(platform);
    if (isAppFlow) {
        return ADDITIONAL_API_HEADERS_FOR_PL_APIS;
    }
    return {};
};
